import { z } from 'zod/v4';

// ========== Auth Schemas ==========

export const startAuthSchema = z.object({
  phoneNumber: z.string({ message: 'Номер телефона обязателен' })
    .min(5, 'Номер телефона слишком короткий (минимум 5 символов)')
    .max(20, 'Номер телефона слишком длинный (максимум 20 символов)'),
});

export type StartAuthDto = z.infer<typeof startAuthSchema>;

export const verifyCodeSchema = z.object({
  code: z.string({ message: 'Код подтверждения обязателен' })
    .min(4, 'Код должен быть минимум 4 символа')
    .max(6, 'Код должен быть максимум 6 символов'),
});

export type VerifyCodeDto = z.infer<typeof verifyCodeSchema>;

export const verify2FASchema = z.object({
  password: z.string({ message: 'Пароль 2FA обязателен' })
    .min(1, 'Пароль 2FA обязателен'),
});

export type Verify2FADto = z.infer<typeof verify2FASchema>;

// ========== Message Schemas ==========

export const telegramSendMessageSchema = z.object({
  text: z.string().optional(),
  replyTo: z.number().optional(),
});

export type TelegramSendMessageDto = z.infer<typeof telegramSendMessageSchema>;

export const markPublicSchema = z.object({
  telegramMessageIds: z.array(z.number()).min(1, 'Требуется хотя бы один ID сообщения'),
  isPublic: z.boolean({ message: 'Флаг публичности обязателен' }),
  orderId: z.number().int('ID заказа должен быть целым числом').positive('ID заказа должен быть положительным').optional(),
});

export type MarkPublicDto = z.infer<typeof markPublicSchema>;

export const linkChatSchema = z.object({
  orderId: z.number({ message: 'ID заказа обязателен' }).int('ID заказа должен быть целым числом'),
});

export type LinkChatDto = z.infer<typeof linkChatSchema>;

// Entity schema для форматирования текста (как в Telegram Web A)
export const messageEntitySchema = z.object({
  type: z.enum([
    'bold', 'italic', 'underline', 'strikethrough', 'code', 'pre',
    'text_link', 'mention', 'mention_name', 'hashtag', 'cashtag', 'bot_command',
    'url', 'email', 'phone_number', 'spoiler', 'blockquote', 'custom_emoji',
  ], { message: 'Тип форматирования обязателен' }),
  offset: z.number({ message: 'Смещение обязательно' }).int('Смещение должно быть целым числом').min(0, 'Смещение не может быть отрицательным'),
  length: z.number({ message: 'Длина обязательна' }).int('Длина должна быть целым числом').positive('Длина должна быть положительной'),
  url: z.string().url('Некорректный URL').optional(),
  language: z.string().optional(),
  userId: z.number().int('ID пользователя должен быть целым числом').optional(),
  documentId: z.string().optional(),
});

export type MessageEntityDto = z.infer<typeof messageEntitySchema>;

export const telegramEditMessageSchema = z.object({
  text: z.string({ message: 'Текст сообщения обязателен' }).min(1, 'Текст сообщения не может быть пустым'),
  entities: z.array(messageEntitySchema).optional(),
  noWebPage: z.boolean().optional(),
});

export type TelegramEditMessageDto = z.infer<typeof telegramEditMessageSchema>;

export const forwardMessageSchema = z.object({
  toChatId: z.number({ message: 'ID чата обязателен' }).int('ID чата должен быть целым числом'),
  noAuthors: z.boolean().optional(),
  noCaptions: z.boolean().optional(),
  silent: z.boolean().optional(),
});

export type ForwardMessageDto = z.infer<typeof forwardMessageSchema>;

export const forwardMessagesSchema = z.object({
  telegramMessageIds: z.array(z.number()).min(1, 'Требуется хотя бы один ID сообщения'),
  toChatId: z.number({ message: 'ID чата обязателен' }).int('ID чата должен быть целым числом'),
  noAuthors: z.boolean().optional(),
  noCaptions: z.boolean().optional(),
  silent: z.boolean().optional(),
});

export type ForwardMessagesDto = z.infer<typeof forwardMessagesSchema>;

// ========== Typing & Drafts ==========

export const typingActionSchema = z.object({
  action: z.enum([
    'typing', 'cancel', 'record_voice', 'record_video',
    'upload_photo', 'upload_document', 'upload_video',
  ]).optional().default('typing'),
});

export type TypingActionDto = z.infer<typeof typingActionSchema>;

export const saveDraftSchema = z.object({
  text: z.string(),
  replyToMsgId: z.number().optional(),
});

export type SaveDraftDto = z.infer<typeof saveDraftSchema>;

export const markAsReadSchema = z.object({
  maxId: z.number().optional(),
});

export type MarkAsReadDto = z.infer<typeof markAsReadSchema>;

// ========== File Schemas ==========

export const sendStickerSchema = z.object({
  stickerId: z.string({ message: 'ID стикера обязателен' }).min(1, 'ID стикера обязателен'),
  replyTo: z.number().int('ID сообщения должен быть целым числом').optional(),
});

export type SendStickerDto = z.infer<typeof sendStickerSchema>;

export const sendStickerByDocumentSchema = z.object({
  documentId: z.string({ message: 'ID документа обязателен' }).min(1, 'ID документа обязателен'),
  accessHash: z.string({ message: 'accessHash обязателен' }).min(1, 'accessHash обязателен'),
  fileReference: z.string({ message: 'fileReference обязателен' }).min(1, 'fileReference обязателен'),
  replyTo: z.number().int('ID сообщения должен быть целым числом').optional(),
});

export type SendStickerByDocumentDto = z.infer<typeof sendStickerByDocumentSchema>;

export const sendGifSchema = z.object({
  documentId: z.string({ message: 'ID документа обязателен' }).min(1, 'ID документа обязателен'),
  accessHash: z.string({ message: 'accessHash обязателен' }).min(1, 'accessHash обязателен'),
  fileReference: z.string({ message: 'fileReference обязателен' }).min(1, 'fileReference обязателен'),
  replyTo: z.number().int('ID сообщения должен быть целым числом').optional(),
});

export type SendGifDto = z.infer<typeof sendGifSchema>;

export const shareSessionSchema = z.object({
  adminUserId: z.number({ message: 'ID сотрудника обязателен' }).int('ID должен быть целым числом').positive('Некорректный ID сотрудника'),
});

export type ShareSessionDto = z.infer<typeof shareSessionSchema>;
